// Cell -> typed value coercion, shared by the mapping step and the row builder.
//
// Split out of ImportRow.ts because ColumnMapping.ts now has to decide whether a file
// column carries a FULL date (so one date column can feed both a month field and a year
// field). ImportRow.ts imports ColumnMapping.ts, so the parsers could not stay there
// without creating a cycle.
//
// Everything funnels through parseDateParts, which returns whatever parts a value
// actually carries rather than a date. That distinction is the whole point: "3" carries
// a month (and, read as a year, 2003) but is not a date, whereas "Mar-26" and
// "2026-03-15" carry both a month and a year and therefore ARE one. Only the latter can
// legitimately fill a month column and a year column from the same cell.
import dayjs from 'dayjs';
import { monthToNumber } from '../../../../Helpers';

/**
 * Below this, a bare number is a value rather than an Excel serial date.
 *
 * The previous threshold was 1000, which made parseYearValue(2026) read 2026 as a
 * serial and return 1905 - a year column full of plain numbers came out a century
 * wrong. Real serials in customer files are >= ~30000 (1982 onward), so 3000 keeps a
 * wide margin while leaving every plausible year (1900-2999) to be read as a year.
 */
const MIN_EXCEL_SERIAL = 3000;

/** Days between the Excel epoch (1899-12-30) and the Unix epoch. */
const EXCEL_EPOCH_OFFSET_DAYS = 25569;

/** "Mar-26", "Mar 2026", "March, 2026" - the commonest budget-month spelling. */
const MONTH_NAME_THEN_YEAR = /^([a-z]{3,9})[\s,\-/.]+(\d{2}|\d{4})$/i;
/** "2026 Mar", "2026-March" */
const YEAR_THEN_MONTH_NAME = /^(\d{4})[\s,\-/.]+([a-z]{3,9})$/i;
/** "2026-03", "2026/3" */
const YEAR_THEN_MONTH_NUMBER = /^(\d{4})[\s\-/.](\d{1,2})$/;
/** "03/2026", "3-2026". Four-digit year required - "3/26" is too ambiguous to guess. */
const MONTH_NUMBER_THEN_YEAR = /^(\d{1,2})[\s\-/.](\d{4})$/;
const NUMERIC_TEXT = /^-?\d+(\.\d+)?$/;
const HAS_SEPARATOR = /[\s\-/.,]/;

export interface DateParts {
    year?: number;
    month?: number;
    day?: number;
    /**
     * The value pins down a month AND a year on its own. False for values that only
     * carry one part, and for bare small numbers where month and year are just two
     * readings of the same digits.
     */
    isFullDate: boolean;
}

/** Two-digit years in commodity files are always this century: 26 -> 2026. */
function expandTwoDigitYear(value: number): number {
    return value < 100 ? 2000 + value : value;
}

/**
 * Excel serial -> date parts, read in UTC.
 *
 * The serial converts to a UTC midnight, so reading it back with local getters lands on
 * the previous day for every user west of UTC - and a serial for the 1st of a month
 * then reports the month before, quietly filing a budget row against the wrong month.
 */
function fromExcelSerial(serial: number): DateParts | null {
    const asUtc = new Date(Math.round((serial - EXCEL_EPOCH_OFFSET_DAYS) * 86400 * 1000));
    if (isNaN(asUtc.getTime())) return null;
    return {
        year: asUtc.getUTCFullYear(),
        month: asUtc.getUTCMonth() + 1,
        day: asUtc.getUTCDate(),
        isFullDate: true,
    };
}

/**
 * Pull whatever date parts a raw cell carries. Returns null when it carries none.
 *
 * Callers pick the part they need (see parseMonthValue / parseYearValue), which is how
 * the month/year ambiguity of a small number stays resolved by the target field rather
 * than guessed here.
 */
export function parseDateParts(value: unknown): DateParts | null {
    if (value === undefined || value === null || value === '') return null;

    if (value instanceof Date) {
        const parsed = dayjs(value);
        if (!parsed.isValid()) return null;
        return { year: parsed.year(), month: parsed.month() + 1, day: parsed.date(), isFullDate: true };
    }

    if (typeof value === 'number') {
        if (isNaN(value)) return null;
        if (value > MIN_EXCEL_SERIAL) return fromExcelSerial(value);
        if (value >= 1900 && value <= 2999) return { year: value, isFullDate: false };
        // 1-12 reads as either a month or a two-digit year; 13-99 only as a year.
        // Both are offered, neither is a full date.
        if (value >= 0 && value < 100 && Number.isInteger(value)) {
            return {
                month: value >= 1 && value <= 12 ? value : undefined,
                year: expandTwoDigitYear(value),
                isFullDate: false,
            };
        }
        return null;
    }

    const text = String(value).trim();
    if (!text) return null;

    if (NUMERIC_TEXT.test(text)) return parseDateParts(Number(text));

    const monthThenYear = text.match(MONTH_NAME_THEN_YEAR);
    if (monthThenYear) {
        const month = monthToNumber(monthThenYear[1]);
        if (month) {
            return {
                month,
                year: expandTwoDigitYear(Number(monthThenYear[2])),
                isFullDate: true,
            };
        }
    }

    const yearThenMonth = text.match(YEAR_THEN_MONTH_NAME);
    if (yearThenMonth) {
        const month = monthToNumber(yearThenMonth[2]);
        if (month) return { month, year: Number(yearThenMonth[1]), isFullDate: true };
    }

    const yearThenNumber = text.match(YEAR_THEN_MONTH_NUMBER);
    if (yearThenNumber) {
        const month = Number(yearThenNumber[2]);
        if (month >= 1 && month <= 12) {
            return { month, year: Number(yearThenNumber[1]), isFullDate: true };
        }
    }

    const numberThenYear = text.match(MONTH_NUMBER_THEN_YEAR);
    if (numberThenYear) {
        const month = Number(numberThenYear[1]);
        if (month >= 1 && month <= 12) {
            return { month, year: Number(numberThenYear[2]), isFullDate: true };
        }
    }

    const byName = monthToNumber(text);
    if (byName) return { month: byName, isFullDate: false };

    // Anything left with a separator in it - ISO dates, "3/15/2026", "March 15, 2026".
    // The separator guard keeps dayjs from accepting bare digit runs as dates.
    if (HAS_SEPARATOR.test(text)) {
        const parsed = dayjs(text);
        if (parsed.isValid()) {
            return {
                year: parsed.year(),
                month: parsed.month() + 1,
                day: parsed.date(),
                isFullDate: true,
            };
        }
    }

    return null;
}

/** True when one cell can fill a month field and a year field on its own. */
export function isFullDateValue(value: unknown): boolean {
    return parseDateParts(value)?.isFullDate === true;
}

/**
 * Whether a column's values are full dates, from a sample. Used to decide whether the
 * month/year halves of a date group may share this column.
 */
export function columnHoldsFullDates(values: readonly unknown[] | undefined): boolean {
    if (!values || values.length === 0) return false;
    const populated = values.filter(v => v !== undefined && v !== null && v !== '');
    if (populated.length === 0) return false;
    return populated.every(isFullDateValue);
}

export function parseDateValue(value: unknown): dayjs.Dayjs | null {
    const parts = parseDateParts(value);
    if (!parts || parts.year === undefined || parts.month === undefined) return null;
    // A month/year with no day (e.g. "Mar-26") anchors to the first of the month, which
    // is what every other date-from-month path in this app does.
    return dayjs(new Date(parts.year, parts.month - 1, parts.day ?? 1));
}

export function parseMonthValue(value: unknown): number | undefined {
    const month = parseDateParts(value)?.month;
    return month !== undefined && month >= 1 && month <= 12 ? month : undefined;
}

export function parseYearValue(value: unknown): number | undefined {
    const year = parseDateParts(value)?.year;
    return year !== undefined && year >= 1900 && year <= 2999 ? year : undefined;
}

export function parseNumberValue(value: unknown): number | undefined {
    if (value === undefined || value === null || value === '') return undefined;
    if (typeof value === 'number') return isNaN(value) ? undefined : value;
    // Tolerate thousands separators, currency symbols and parenthesised negatives.
    const text = String(value).trim();
    const negative = /^\(.*\)$/.test(text);
    const cleaned = text.replace(/[()]/g, '').replace(/[^0-9.\-+eE]/g, '');
    if (!cleaned) return undefined;
    const parsed = Number(cleaned);
    if (isNaN(parsed)) return undefined;
    return negative ? -parsed : parsed;
}
